import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/**
 * Who is signed in, and what they may do.
 *
 * The access token is **not** here: it lives in the axios layer, in memory, and a
 * token in the Redux store is a token in every devtools export and every state
 * snapshot somebody pastes into a bug report.
 */
export interface SessionUser {
  fullName: string
  roleName: string
  permissions: string[]
  /** Every permission mapped to the roles holding it, so a refusal can name one. */
  rolesWith: Record<string, string[]>
}

export interface SessionState {
  user: SessionUser | null
  /** True until the refresh cookie has been tried once, so nothing flashes sign-in. */
  restoring: boolean
}

const initialState: SessionState = { user: null, restoring: true }

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    signedIn: (s, a: PayloadAction<SessionUser>) => {
      s.user = a.payload
      s.restoring = false
    },
    signedOut: (s) => {
      s.user = null
      s.restoring = false
    },
    restoreFinished: (s) => void (s.restoring = false),
  },
  selectors: {
    selectUser: (s) => s.user,
    selectRestoring: (s) => s.restoring,
  },
})

export const { signedIn, signedOut, restoreFinished } = sessionSlice.actions
export const { selectUser, selectRestoring } = sessionSlice.selectors
